import React, { useState, useEffect } from 'react';
import { supabase } from '../utils/supabase';
import { PlusCircle, Trash2 } from 'lucide-react';

interface ComplimentaryAccess {
  id: string;
  user_email: string;
  access_type: '6_months' | '12_months';
  start_date: string;
  end_date: string;
}

export default function AdminDashboard() {
  const [isAdmin, setIsAdmin] = useState(false);
  const [accessGrants, setAccessGrants] = useState<ComplimentaryAccess[]>([]);
  const [newGrant, setNewGrant] = useState({
    email: '',
    accessType: '6_months' as '6_months' | '12_months'
  });

  useEffect(() => {
    checkAdminStatus();
    fetchAccessGrants();
  }, []);

  const checkAdminStatus = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (user) {
      const { data: profile } = await supabase
        .from('user_profiles')
        .select('is_admin')
        .eq('id', user.id)
        .single();
      
      setIsAdmin(profile?.is_admin || false);
    }
  };

  const fetchAccessGrants = async () => {
    const { data, error } = await supabase
      .from('complimentary_access')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching access grants:', error);
      return;
    }

    setAccessGrants(data);
  };

  const handleGrantAccess = async (e: React.FormEvent) => {
    e.preventDefault();

    const months = newGrant.accessType === '6_months' ? 6 : 12;
    const endDate = new Date();
    endDate.setMonth(endDate.getMonth() + months);

    const { error } = await supabase
      .from('complimentary_access')
      .insert([
        {
          user_email: newGrant.email,
          access_type: newGrant.accessType,
          end_date: endDate.toISOString()
        }
      ]);

    if (error) {
      console.error('Error granting access:', error);
      alert('Failed to grant access. Please try again.');
      return;
    }

    setNewGrant({ email: '', accessType: '6_months' });
    fetchAccessGrants();
  };

  const handleRevokeAccess = async (id: string) => {
    const { error } = await supabase
      .from('complimentary_access')
      .delete()
      .eq('id', id);

    if (error) {
      console.error('Error revoking access:', error);
      alert('Failed to revoke access. Please try again.');
      return;
    }

    fetchAccessGrants();
  };

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Access Denied</h1>
          <p className="text-gray-600">You do not have permission to view this page.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <div className="bg-white rounded-lg shadow p-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-8">Manage Complimentary Access</h1>

          {/* Grant Access Form */}
          <form onSubmit={handleGrantAccess} className="mb-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <input
                type="email"
                value={newGrant.email}
                onChange={(e) => setNewGrant(prev => ({ ...prev, email: e.target.value }))}
                placeholder="User Email"
                className="rounded-lg border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                required
              />
              <select
                value={newGrant.accessType}
                onChange={(e) => setNewGrant(prev => ({ ...prev, accessType: e.target.value as '6_months' | '12_months' }))}
                className="rounded-lg border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
              >
                <option value="6_months">6 Months</option>
                <option value="12_months">12 Months</option>
              </select>
              <button
                type="submit"
                className="flex items-center justify-center gap-2 bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 transition-colors"
              >
                <PlusCircle className="w-5 h-5" />
                Grant Access
              </button>
            </div>
          </form>

          {/* Access Grants Table */}
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Access Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Start Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">End Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {accessGrants.map((grant) => (
                  <tr key={grant.id}>
                    <td className="px-6 py-4 whitespace-nowrap">{grant.user_email}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {grant.access_type === '6_months' ? '6 Months' : '12 Months'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {new Date(grant.start_date).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {new Date(grant.end_date).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        onClick={() => handleRevokeAccess(grant.id)}
                        className="text-red-600 hover:text-red-800 transition-colors"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}